import type { Permission } from '../api/types'
import type { IconName } from './Icon'

export type NavMode = 'simple' | 'developer'

export interface NavItem {
  label: string
  path: string
  icon: IconName
  soon?: boolean
  /** Hidden unless the active workspace's plan carries this boolean entitlement key. */
  entitlement?: string
  /** Shown only when the session holds this permission (rbac-restore D52, `can`). */
  permission?: Permission
}

// Simple mode (D31): what every user sees, Developer mode or not.
export const SIMPLE_NAV: NavItem[] = [
  { label: 'Apps', path: '/apps', icon: 'grid' },
  { label: 'Connect to Claude', path: '/connect-claude', icon: 'spark', entitlement: 'connect_claude' },
  { label: 'Team', path: '/team', icon: 'users' },
  { label: 'Workspace', path: '/workspace', icon: 'box' },
  { label: 'Account', path: '/account', icon: 'settings' },
]

// The role × permission page (/permissions, rbac-restore R1) is not a nav item: it is
// reached from Team in both modes (simple-mode S3), so it is routed outside the guard.

// Developer console, shown below the simple items only when Developer mode is
// effective (preference AND entitlement, D30). Every path here must also be routed
// inside <RequireDeveloperMode> in Shell.tsx (Shell.test asserts each one redirects
// to /apps when Developer mode is off). Add new developer pages here.
export const DEVELOPER_NAV_GROUPS: NavItem[][] = [
  [
    { label: 'Getting Started', path: '/getting-started', icon: 'rocket' },
    { label: 'Playground', path: '/playground', icon: 'play' },
    { label: 'API Keys', path: '/api-keys', icon: 'key' },
  ],
  [
    { label: 'Toolkits', path: '/toolkits', icon: 'grid' },
    { label: 'MCP Directory', path: '/directory', icon: 'globe' },
    { label: 'Skills', path: '/skills', icon: 'spark' },
  ],
  [
    // Plan-gated (S4): hidden when the plan lacks the feature; a deep link still renders
    // the page with the remedy and the upsell link.
    { label: 'Users', path: '/users', icon: 'users', entitlement: 'platform.end_users' },
    { label: 'Sessions', path: '/sessions', icon: 'clock', entitlement: 'platform.end_users' },
    { label: 'Auth Configs', path: '/auth-configs', icon: 'shield', entitlement: 'auth_configs.byo' },
    { label: 'Triggers', path: '/triggers', icon: 'bolt' },
    // rbac-restore R2 (developer mode): Webhooks.
    { label: 'Webhooks', path: '/webhooks', icon: 'link', permission: 'configure_webhooks' },
    { label: 'Logs', path: '/logs', icon: 'list' },
    { label: 'Sandbox', path: '/sandbox', icon: 'box', soon: true },
    { label: 'Settings', path: '/settings', icon: 'settings' },
  ],
]

export const DEVELOPER_NAV: NavItem[] = DEVELOPER_NAV_GROUPS.flat()

/** Every nav item in both modes (simple first). */
export const ALL_NAV: NavItem[] = [...SIMPLE_NAV, ...DEVELOPER_NAV]

/**
 * The sidebar groups for the current mode: the simple group always, then the developer
 * groups when Developer mode is effective. `entitled(key)` filters entitlement-gated items;
 * `permitted(permission)` filters permission-gated items (rbac-restore D52). Both the
 * sidebar and the page search use these groups, so a hidden item is never searchable.
 */
export function navGroups(
  effective: boolean,
  entitled: (key: string) => boolean = () => true,
  permitted: (permission: Permission) => boolean = () => true,
): NavItem[][] {
  const show = (n: NavItem) => (!n.entitlement || entitled(n.entitlement)) && (!n.permission || permitted(n.permission))
  const groups = effective ? [SIMPLE_NAV, ...DEVELOPER_NAV_GROUPS] : [SIMPLE_NAV]
  return groups.map((g) => g.filter(show))
}
